import { computed } from 'vue';
import { ScheduledCampaignValidationIssue } from '@studyportals/university-scheduler-interface';
import { ValidationStateResponse } from '@studyportals/campaign-management-api-interface/src/domain/campaign/validation-state.response';
import { CampaignType } from '@studyportals/campaign-management-api-interface/src/domain/campaign/enums/campaign-type.enum';
import { Routes } from '@/models/router/router.interface';
import store from '@/store';

class CampaignReadinessPresenter {

	public campaignIssuesAsSentences = computed((): string[] => {
		if (!this.campaignReadinessInfo.value ||
			this.campaignReadinessInfo.value.campaignType === CampaignType.CUSTOM_PRODUCTS ||
			!this.isCampaignInvalid.value) {
			return [];
		}

		return this.campaignReadinessInfo.value.issues.map((issue: ScheduledCampaignValidationIssue) => {
			return this.translateIssueIntoSentence(issue);
		});
	});

	public isCampaignValid = computed((): boolean => {
		const campaignReadinessInfo = this.campaignReadinessInfo.value;
		if (!campaignReadinessInfo) {
			return false;
		}

		return campaignReadinessInfo.valid;
	});

	public isCampaignInvalid = computed((): boolean => {
		const campaignReadinessInfo = this.campaignReadinessInfo.value;
		if (!campaignReadinessInfo) {
			return false;
		}

		return !campaignReadinessInfo.valid;
	});

	private campaignReadinessInfo = computed((): ValidationStateResponse | null => {
		return store.getters.campaignReadinessInfo();
	});

	public translateIssueIntoSentence(issue: ScheduledCampaignValidationIssue): string {
		switch (issue) {
			case ScheduledCampaignValidationIssue.NoEvents:
				return 'Start date and end date not found';
			case ScheduledCampaignValidationIssue.NoStartEvent:
				return 'Start date not found';
			case ScheduledCampaignValidationIssue.StartEventDateInPastNotExecuted:
			case ScheduledCampaignValidationIssue.StartEventDateTodayNotExecuted:
				return 'Campaign could not be automatically started on start date';
			case ScheduledCampaignValidationIssue.StartEventDateInFutureAlreadyExecuted:
				return 'Campaign was automatically started before start date';
			case ScheduledCampaignValidationIssue.NoEntitiesAttachedToStartEvent:
				return 'No studies or organisations attached to the campaign';
			case ScheduledCampaignValidationIssue.NoTargetedCountriesAttachedToStartEvent:
				return 'Premiumness per country is not set up';
			case ScheduledCampaignValidationIssue.MultipleStartEvents:
				return 'Multiple start dates found';
			case ScheduledCampaignValidationIssue.StartEventDateAfterEndEventDate:
				return 'The start date is after the end date';
			case ScheduledCampaignValidationIssue.NoPricingProducts:
				return 'One or more of the attached entities has no pricing products attached';
			case ScheduledCampaignValidationIssue.UnexpectedPricingProductsCount:
				return 'One or more of the attached entities have an unexpected number of pricing products attached';
			case ScheduledCampaignValidationIssue.NoEndEvent:
				return 'End date not found';
			case ScheduledCampaignValidationIssue.EndEventDateInPastNotExecuted:
			case ScheduledCampaignValidationIssue.EndEventDateTodayNotExecuted:
				return 'Campaign could not be automatically ended on end date';
			case ScheduledCampaignValidationIssue.EndEventDateInFutureAlreadyExecuted:
				return 'Campaign was automatically ended before end date';
			case ScheduledCampaignValidationIssue.MultipleEndEvents:
				return 'Multiple end dates found';
			default:
				return 'Invalid for an unknown reason';
		}
	}

	public doesPageRequireAction(path: Routes): boolean {
		if (!this.campaignReadinessInfo.value ||
			this.campaignReadinessInfo.value.campaignType === CampaignType.CUSTOM_PRODUCTS) {
			return false;
		}

		return this.campaignReadinessInfo.value.issues.find((issue: ScheduledCampaignValidationIssue) => {
			return this.isPageRelevantForIssue(path, issue);
		}) !== undefined;
	}

	private isPageRelevantForIssue(path: Routes, issue: ScheduledCampaignValidationIssue): boolean {
		switch (path) {
			case Routes.GENERAL:
				return [
					ScheduledCampaignValidationIssue.NoEvents,
					ScheduledCampaignValidationIssue.NoStartEvent,
					ScheduledCampaignValidationIssue.StartEventDateInPastNotExecuted,
					ScheduledCampaignValidationIssue.StartEventDateTodayNotExecuted,
					ScheduledCampaignValidationIssue.StartEventDateInFutureAlreadyExecuted,
					ScheduledCampaignValidationIssue.MultipleStartEvents,
					ScheduledCampaignValidationIssue.StartEventDateAfterEndEventDate,
					ScheduledCampaignValidationIssue.NoEndEvent,
					ScheduledCampaignValidationIssue.EndEventDateInPastNotExecuted,
					ScheduledCampaignValidationIssue.EndEventDateTodayNotExecuted,
					ScheduledCampaignValidationIssue.EndEventDateInFutureAlreadyExecuted,
					ScheduledCampaignValidationIssue.MultipleEndEvents
				].includes(issue);
			case Routes.ENTITIES_AND_GEOTARGETING:
				return [
					ScheduledCampaignValidationIssue.NoEntitiesAttachedToStartEvent,
					ScheduledCampaignValidationIssue.NoTargetedCountriesAttachedToStartEvent,
					ScheduledCampaignValidationIssue.NoPricingProducts,
					ScheduledCampaignValidationIssue.UnexpectedPricingProductsCount
				].includes(issue);
			default:
				return false;
		}
	}
}

export default new CampaignReadinessPresenter();
